import { http } from "./http";

export const SELECTED_ESTABLISHMENT_ID_KEY = "selected-establishment-id";

export type EstablishmentPublic = {
  id: string;
  name: string;
  slug: string;
  billingActive?: number | boolean | null;
  logoUrl?: string | null;
  /**
   * Ícone do app instalado gerado pelo backend a partir do logo (versão de 12
   * hex). `null` sem ícone possível; ausente nas respostas 403 e 404.
   */
  pwaIcon?: { version: string } | null;
  backgroundColor?: string | null;
  backgroundImageUrl?: string | null;
  headerTitleColor?: string | null;
  headerDescriptionColor?: string | null;
  infoBoxBackgroundColor?: string | null;
  infoBoxTitleColor?: string | null;
  infoBoxTextColor?: string | null;
  infoBoxOpacity?: number | null;
  infoBoxBlur?: number | null;
  bannerOverlayOpacity?: number | null;
  aboutText?: string | null;
  allowAnyProvider?: boolean | number | null;
  allowMultiServices?: boolean; // adicionada para controlar multi-serviços no público/admin
  socialInstagram?: string | null;
  socialTiktok?: string | null;
  socialYoutube?: string | null;
  socialWhatsapp?: string | null;
  cep?: string | null;
  addressLine?: string | null;
  addressNumber?: string | null;
  addressComplement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  mapLat?: number | null;
  mapLng?: number | null;
  timezone: string;
  slotDurationMinutes: number;
  passCardFeesToClient?: "SIM" | "NÃO" | null;
  reminderLeadTimeMinutes?: number | null;
  /**
   * CNF-04: a unidade manda a confirmação do agendamento pelo WhatsApp (chave
   * ligada, sessão utilizável e assinatura ativa). A rota omite o campo quando
   * bloqueia com 403; ausente vale falso.
   */
  whatsappBookingConfirmation?: boolean;
  reminderPushEnabled?: boolean;
  reminderEmailEnabled?: boolean;
  subscriptionStatus?: string;
  subscriptionActive?: boolean;
  currentPeriodEnd?: string | null;
  cancel_at_period_end?: boolean;
  hasActiveAccess?: boolean;
};

export type MyEstablishment = EstablishmentPublic & {
  id: string;
  membershipRole?: "owner" | "admin" | "member";
};

export function getSelectedEstablishmentIdFromStorage(): string | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(SELECTED_ESTABLISHMENT_ID_KEY);
  return raw ? String(raw).trim() || null : null;
}

export function setSelectedEstablishmentIdInStorage(
  establishmentId: string | null
): void {
  if (typeof window === "undefined") return;
  if (!establishmentId) {
    window.localStorage.removeItem(SELECTED_ESTABLISHMENT_ID_KEY);
    return;
  }
  window.localStorage.setItem(SELECTED_ESTABLISHMENT_ID_KEY, establishmentId);
}

export async function getMyEstablishment(token: string, establishmentId?: string | null) {
  const selectedEstablishmentId =
    establishmentId || getSelectedEstablishmentIdFromStorage();
  try {
    return await http<MyEstablishment>(`/establishments/me`, {
      token,
      query: {
        establishmentId: selectedEstablishmentId || undefined,
      },
    });
  } catch (error: any) {
    if (selectedEstablishmentId && (error?.status === 404 || error?.status === 403)) {
      setSelectedEstablishmentIdInStorage(null);
      return http<MyEstablishment>(`/establishments/me`, {
        token,
      });
    }
    throw error;
  }
}

export async function listMyEstablishments(token: string) {
  return http<MyEstablishment[]>(`/establishments`, { token });
}

export async function adminListAllEstablishments(token: string) {
  return http<
    Array<{
      id: string;
      name: string;
      slug: string;
      timezone: string;
      createdAt: string;
      servicesCount: number;
      providersCount: number;
      appointmentsTotal: number;
    }>
  >(`/admin/establishments/all`, { token });
}

export async function checkEstablishmentSlugAvailability(
  token: string,
  slug: string,
  excludeId?: string
) {
  return http<{ available: boolean }>(`/establishments/slug-availability`, {
    token,
    query: {
      slug,
      excludeId,
    },
  });
}

export async function createEstablishment(
  token: string,
  payload: {
    name: string;
    slug: string;
    logoUrl?: string | null;
    backgroundColor?: string | null;
    backgroundImageUrl?: string | null;
    headerTitleColor?: string | null;
    headerDescriptionColor?: string | null;
    aboutText?: string | null;
    socialInstagram?: string | null;
    socialTiktok?: string | null;
    socialYoutube?: string | null;
    socialWhatsapp?: string | null;
    cep?: string | null;
    addressLine?: string | null;
    addressNumber?: string | null;
    addressComplement?: string | null;
    neighborhood?: string | null;
    city?: string | null;
    state?: string | null;
    mapLat?: number | null;
    mapLng?: number | null;
    timezone: string;
    slotDurationMinutes?: number;
    allowAnyProvider?: boolean;
    billingActive?: boolean;
    billingRemovalTargetId?: string | null;
    billingUseTemporarySlot?: boolean;
    infoBoxBackgroundColor?: string | null;
    infoBoxTitleColor?: string | null;
    infoBoxTextColor?: string | null;
    infoBoxOpacity?: number | null;
    infoBoxBlur?: number | null;
    bannerOverlayOpacity?: number | null;
  }
) {
  return http<MyEstablishment>(`/establishments`, {
    method: "POST",
    body: {
      ...payload,
      headerTitleColor: payload.headerTitleColor ?? null,
      headerDescriptionColor: payload.headerDescriptionColor ?? null,
      infoBoxBackgroundColor: payload.infoBoxBackgroundColor ?? null,
      infoBoxTitleColor: payload.infoBoxTitleColor ?? null,
      infoBoxTextColor: payload.infoBoxTextColor ?? null,
      infoBoxOpacity: payload.infoBoxOpacity ?? null,
      infoBoxBlur: payload.infoBoxBlur ?? null,
      bannerOverlayOpacity: payload.bannerOverlayOpacity ?? null,
    },
    token,
  });
}

export async function migrateEstablishmentData(
  token: string,
  targetEstablishmentId: string,
  payload: {
    sourceEstablishmentId: string;
    copyServices?: boolean;
    copyProviders?: boolean;
    copyWeeklyAvailability?: boolean;
    copyBlackoutDates?: boolean;
    overwrite?: boolean;
  }
) {
  return http<{
    ok: boolean;
    sourceEstablishmentId: string;
    targetEstablishmentId: string;
    overwrite: boolean;
    copied: {
      servicesCopied: number;
      providersCopied: number;
      providerServicesCopied: number;
      providerWeeklyCopied: number;
      weeklyAvailabilityCopied: number;
      blackoutDatesCopied: number;
    };
  }>(`/establishments/${targetEstablishmentId}/migrate-data`, {
    method: "POST",
    token,
    body: payload,
  });
}

export async function updateEstablishment(
  id: string,
  token: string,
  payload: Partial<{
    name: string;
    slug: string;
    logoUrl?: string | null;
    backgroundColor?: string | null;
    backgroundImageUrl?: string | null;
    headerTitleColor?: string | null;
    headerDescriptionColor?: string | null;
    aboutText?: string | null;
    allowMultiServices?: boolean;
    allowAnyProvider?: boolean;
    infoBoxBackgroundColor?: string | null;
    infoBoxTitleColor?: string | null;
    infoBoxTextColor?: string | null;
    infoBoxOpacity?: number | null;
    infoBoxBlur?: number | null;
    bannerOverlayOpacity?: number | null;
    socialInstagram?: string | null;
    socialTiktok?: string | null;
    socialYoutube?: string | null;
    socialWhatsapp?: string | null;
    cep?: string | null;
    addressLine?: string | null;
    addressNumber?: string | null;
    addressComplement?: string | null;
    neighborhood?: string | null;
    city?: string | null;
    state?: string | null;
    mapLat?: number | null;
    mapLng?: number | null;
    timezone: string;
    slotDurationMinutes: number;
    passCardFeesToClient?: "SIM" | "NÃO";
  }>
) {
  // CAD-16: atualização parcial. Só vai o que foi informado; `null` explícito
  // limpa o campo. Forçar `?? null` aqui apagava a personalização do header
  // sempre que outra tela (multi-serviços, repasse de taxas) salvava um campo.
  return http<MyEstablishment>(`/establishments/${id}`, {
    method: "PUT",
    body: payload,
    token,
  });
}

export async function setEstablishmentBillingActive(
  id: string,
  token: string,
  active: boolean,
  billingRemovalTargetId?: string | null,
  billingUseTemporarySlot?: boolean
) {
  return http<{ id: string; active: boolean }>(`/establishments/${id}/billing-active`, {
    method: "PUT",
    body: {
      active,
      billingRemovalTargetId: billingRemovalTargetId || null,
      billingUseTemporarySlot: billingUseTemporarySlot === true,
    },
    token,
  });
}

export type EstablishmentUser = {
  id: string;
  email: string;
  fullName?: string | null;
  phone?: string | null;
  role: "owner" | "admin" | "member";
  active?: boolean;
  accessProfileCode?:
    | "owner"
    | "admin"
    | "manager"
    | "professional"
    | "reception"
    | "dashboard_viewer"
    | "member";
  accessScope?: "single_establishment" | "all_owner_establishments";
  permissionsJson?: string | null;
  scopedEstablishmentIdsJson?: string | null;
  scopedEstablishmentIds?: string[];
  linkedProviderId?: string | null;
  linkedProviderName?: string | null;
};
export async function listMyEstablishmentUsers(token: string, establishmentId?: string) {
  return http<EstablishmentUser[]>(`/establishments/me/users`, {
    token,
    query: establishmentId ? { establishmentId } : undefined,
  });
}

export type CustomerInsightSegment =
  | "never_attended"
  | "high_cancellation"
  | "high_no_show"
  | "frequent"
  | "inactive";

export type CustomerInsightItem = {
  id: string;
  customerAccountId: string | null;
  hasEstablishmentLink?: boolean;
  name: string;
  email: string | null;
  whatsapp: string | null;
  phone: string | null;
  photoUrl: string | null;
  totalAppointments: number;
  completedAppointments: number;
  cancelledAppointments: number;
  noShowAppointments: number;
  upcomingAppointments: number;
  attendanceRate: number;
  cancellationRate: number;
  noShowRate: number;
  averageVisitIntervalDays: number | null;
  lastAppointmentAt: string | null;
  nextAppointmentAt: string | null;
  frequencyScore: number;
  segments: CustomerInsightSegment[];
};

export type CustomerInsightsResponse = {
  establishmentId: string;
  items: CustomerInsightItem[];
  summary: {
    totalCustomers: number;
    neverAttended: number;
    highCancellation: number;
    highNoShow: number;
    frequent: number;
    inactive: number;
    subscribersCount?: number;
    subscriptionUpToDateCount?: number;
    subscriptionPendingCount?: number;
  };
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  filters: {
    search: string | null;
    segment: string;
    sortBy: string;
    sortOrder: "asc" | "desc";
    lookbackDays: number;
  };
};

export async function listCustomerInsights(
  token: string,
  params?: {
    establishmentId?: string;
    search?: string;
    segment?: "all" | CustomerInsightSegment;
    sortBy?:
      | "appointments"
      | "no_show_rate"
      | "cancellation_rate"
      | "frequency"
      | "last_appointment"
      | "name";
    sortOrder?: "asc" | "desc";
    page?: number;
    pageSize?: number;
    lookbackDays?: number;
    subscriberFilter?: "all" | "subscribers" | "non_subscribers";
    subscriptionHealthFilter?: "all" | "up_to_date" | "not_up_to_date";
  }
) {
  return http<CustomerInsightsResponse>(`/establishments/me/customers`, {
    token,
    query: params,
  });
}

// ======= Criação de conta de cliente pelo admin =======

// SPEC_DEVIATION: design.md lista `cpfMasked` em AdminCustomerAccount, mas o
// contrato confirmado do backend (POST /establishments/:id/customers) devolve
// `customer: { id, fullName, email, phone, whatsapp }`, sem esse campo.
// Reason: a orientação da tarefa trouxe a resposta real do endpoint já
// implementado no repositório `agendamento`; seguimos essa fonte por ser mais
// atual que o rascunho do design.
export type AdminCustomerAccount = {
  id: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
};

export type AdminCustomerAccountConflict = {
  id: string;
  maskedName: string;
  hasEmail: boolean;
  active: boolean;
};

/**
 * MBR-19: convite de acesso enviado ao cliente criado no balcão. Sem `reason`
 * e com `sent: false`, o e-mail é o mesmo de antes e nada foi enviado.
 */
export type AdminCustomerAccountInvite = {
  sent: boolean;
  email: string | null;
  reason?: "NO_EMAIL" | "SEND_FAILED";
};

export type AdminCustomerAccountResult = {
  ok: boolean;
  customer: AdminCustomerAccount;
  linkedAppointmentsCount: number;
  /** Ausente em backend antigo e na vinculação de conta existente. */
  invite?: AdminCustomerAccountInvite;
};

export type AdminCustomerAccountLinkResult = AdminCustomerAccountResult & {
  alreadyLinked: boolean;
};

export type AdminCustomerAccountAccessResult = {
  ok: boolean;
  customer: AdminCustomerAccount;
  invite?: AdminCustomerAccountInvite;
};

export function createAdminCustomerAccount(
  token: string,
  establishmentId: string,
  payload: {
    fullName: string;
    cpf: string;
    email?: string | null;
    phone?: string | null;
    whatsapp?: string | null;
    sourceCustomerKey?: string | null;
  }
): Promise<AdminCustomerAccountResult> {
  return http<AdminCustomerAccountResult>(
    `/establishments/${establishmentId}/customers`,
    {
      method: "POST",
      token,
      body: payload,
    }
  );
}

export function linkAdminCustomerAccount(
  token: string,
  establishmentId: string,
  customerAccountId: string,
  payload?: { sourceCustomerKey?: string | null }
): Promise<AdminCustomerAccountLinkResult> {
  return http<AdminCustomerAccountLinkResult>(
    `/establishments/${establishmentId}/customers/${customerAccountId}/link`,
    {
      method: "POST",
      token,
      body: payload || {},
    }
  );
}

export function updateAdminCustomerAccountAccess(
  token: string,
  establishmentId: string,
  customerAccountId: string,
  payload: { email: string }
): Promise<AdminCustomerAccountAccessResult> {
  return http<AdminCustomerAccountAccessResult>(
    `/establishments/${establishmentId}/customers/${customerAccountId}/access`,
    {
      method: "POST",
      token,
      body: payload,
    }
  );
}

// Owner: ativar/inativar membro no seu estabelecimento
export async function ownerSetMemberActive(
  token: string,
  userId: string,
  active: boolean,
  establishmentId?: string
) {
  return http<{ userId: string; active: boolean }>(`/establishments/me/users/active`, {
    method: "PUT",
    token,
    body: { userId, active, establishmentId },
  });
}

// Owner: remover membro do seu estabelecimento
export async function ownerRemoveMember(
  token: string,
  userId: string,
  establishmentId?: string
) {
  return http<{ userId: string; removed: boolean }>(`/establishments/me/users`, {
    method: "DELETE",
    token,
    body: { userId, establishmentId },
  });
}

// Owner: alterar papel de membro (member/admin)
export async function ownerSetMemberRole(
  token: string,
  payload: {
    userId: string;
    role: "member" | "admin";
    establishmentId?: string;
    accessProfileCode?:
      | "owner"
      | "admin"
      | "manager"
      | "professional"
      | "reception"
      | "dashboard_viewer"
      | "member";
    accessScope?: "single_establishment" | "all_owner_establishments";
    establishmentIds?: string[];
    permissions?: Record<string, boolean>;
  }
) {
  return http<{
    userId: string;
    role: "member" | "admin";
    accessProfileCode: string;
    accessScope: "single_establishment" | "all_owner_establishments";
    establishmentIds?: string[];
    permissions: Record<string, boolean> | null;
  }>(`/establishments/me/users/role`, {
    method: "PUT",
    token,
    body: payload,
  });
}

// ======= Refund Fee =======

export async function getRefundFeePercentage(establishmentId: string) {
  return http<{ refundFeePercentage: number }>(
    `/establishments/${establishmentId}/refund-fee`
  );
}

export async function updateRefundFeePercentage(
  token: string,
  establishmentId: string,
  refundFeePercentage: number
) {
  return http<{ id: string; refundFeePercentage: number }>(
    `/establishments/${establishmentId}/refund-fee`,
    {
      method: "PUT",
      token,
      body: { refundFeePercentage },
    }
  );
}

// ======= Cancellation Hours (Cancelamento/Reagendamento) =======

export async function getCancellationHours(establishmentId: string) {
  return http<{ cancellationHours: number }>(
    `/establishments/${establishmentId}/cancellation-hours`
  );
}

export async function updateCancellationHours(
  token: string,
  establishmentId: string,
  cancellationHours: number
) {
  return http<{ id: string; cancellationHours: number }>(
    `/establishments/${establishmentId}/cancellation-hours`,
    {
      method: "PUT",
      token,
      body: { cancellationHours },
    }
  );
}

// ======= Reminder Lead Time (Antecedência do lembrete) =======

export type AppointmentReminderSettings = {
  reminderLeadTimeMinutes: number | null;
  reminderPushEnabled: boolean;
  reminderEmailEnabled: boolean;
};

export async function getReminderLeadTime(token: string, establishmentId: string) {
  return http<AppointmentReminderSettings>(
    `/establishments/${establishmentId}/reminder-lead-time`,
    { token }
  );
}

export async function updateReminderLeadTime(
  token: string,
  establishmentId: string,
  reminderLeadTimeMinutesOrSettings: number | null | Partial<AppointmentReminderSettings>
) {
  const body =
    typeof reminderLeadTimeMinutesOrSettings === "object" &&
    reminderLeadTimeMinutesOrSettings !== null
      ? reminderLeadTimeMinutesOrSettings
      : { reminderLeadTimeMinutes: reminderLeadTimeMinutesOrSettings };

  return http<{ id: string } & AppointmentReminderSettings>(
    `/establishments/${establishmentId}/reminder-lead-time`,
    {
      method: "PUT",
      token,
      body,
    }
  );
}
